import { useCallback, useMemo, useState } from 'react';
import type { PaginationState, SortingState } from '@tanstack/react-table';

/**
 * Binds a <DataGrid> to a row set that is already in memory.
 *
 * The counterpart to `useServerDataGrid`, and it exists because not every
 * Stellantis grid is paged on the server. The dealer and regional screens fetch a
 * whole wave in one request — their maps and KPI tiles need the full set anyway —
 * and then page, sort and search it in the browser, which is exactly what the
 * Razor pages did by handing the array to DataTables in client mode.
 *
 * It returns the same `gridProps` shape, so <DataGrid> cannot tell the two apart
 * and stays a single component with one set of markup. The sorting and slicing
 * happen here rather than through TanStack's own row models because the grid
 * declares `manualSorting`/`manualPagination` for the server case; making that
 * conditional would have put a second code path through every grid in the app to
 * save the twenty lines below.
 */

export interface ClientDataGridOptions<TRow> {
  rows: TRow[];
  initialPageSize?: number;
  initialSorting?: SortingState;
  /**
   * Free-text search. Matched case-insensitively against `searchFields`, or
   * against every own string/number property when those are not given.
   */
  search?: string;
  searchFields?: (keyof TRow)[];
  /**
   * Sort value per column id, for columns that do not read straight off the row —
   * a formatted or derived cell. Defaults to `row[columnId]`.
   */
  sortAccessors?: Record<string, (row: TRow) => string | number | null | undefined>;
}

/** Nulls sort last in both directions, so "no value" never leads the table. */
function compareValues(a: unknown, b: unknown): number {
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function matchesSearch<TRow>(row: TRow, term: string, fields?: (keyof TRow)[]): boolean {
  const values = fields
    ? fields.map((field) => row[field])
    : Object.values(row as Record<string, unknown>);

  return values.some((value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'object') return false;
    return String(value).toLowerCase().includes(term);
  });
}

export function useClientDataGrid<TRow>({
  rows,
  initialPageSize = 25,
  initialSorting = [],
  search,
  searchFields,
  sortAccessors,
}: ClientDataGridOptions<TRow>) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const searched = useMemo(() => {
    const term = search?.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => matchesSearch(row, term, searchFields));
  }, [rows, search, searchFields]);

  const sorted = useMemo(() => {
    const sort = sorting[0];
    if (!sort) return searched;

    const read =
      sortAccessors?.[sort.id] ??
      ((row: TRow) => (row as Record<string, unknown>)[sort.id] as string | number | null);

    // Copied before sorting: the array belongs to the query cache, and sorting
    // it in place would mutate what every other consumer of it sees.
    const next = [...searched];
    next.sort((a, b) => compareValues(read(a), read(b)));
    return sort.desc ? next.reverse() : next;
  }, [searched, sorting, sortAccessors]);

  // Filtering can leave the current page past the end of the result set — page 7
  // does not exist once a filter cuts 200 rows down to 30. Clamping here rather
  // than resetting on every change keeps the page number when it is still valid.
  const total = sorted.length;
  const pageIndex = Math.min(pagination.pageIndex, Math.max(0, Math.ceil(total / pagination.pageSize) - 1));

  const page = useMemo(() => {
    const start = pageIndex * pagination.pageSize;
    return sorted.slice(start, start + pagination.pageSize);
  }, [sorted, pageIndex, pagination.pageSize]);

  const resetToFirstPage = useCallback(() => {
    setPagination((current) => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
  }, []);

  return {
    /** Spread straight onto <DataGrid>. */
    gridProps: {
      rows: page,
      total,
      pagination: { ...pagination, pageIndex },
      onPaginationChange: setPagination,
      sorting,
      onSortingChange: setSorting,
      isLoading: false,
      isFetching: false,
    },
    /** Every row that passed the search, in sort order — what an export writes. */
    sortedRows: sorted,
    resetToFirstPage,
  };
}
